import { useRef, useMemo } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Phone } from "lucide-react";
import { scrollToSection } from "../lib/scroll";
import { Eyebrow } from "./Reveal";

const HERO_IMG =
  "https://images.pexels.com/photos/69432/pexels-photo-69432.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=800";

const EASE = [0.22, 1, 0.36, 1];
const LINE_INITIAL = { y: "112%" };
const LINE_ANIMATE = { y: "0%" };
const FADE_UP_INITIAL = { opacity: 0, y: 16 };
const FADE_UP_ANIMATE = { opacity: 1, y: 0 };
const FADE_INITIAL = { opacity: 0 };
const FADE_ANIMATE = { opacity: 1 };
const T_PARAGRAPH = { duration: 0.8, delay: 0.75 };
const T_ACTIONS = { duration: 0.8, delay: 0.9 };
const T_NOTE = { duration: 0.8, delay: 1.1 };
const IMG_INITIAL = { opacity: 0, y: 40 };
const T_IMG = { duration: 1.1, delay: 0.5, ease: EASE };
const SCROLL_OFFSET = ["start start", "end start"];

const HeroLine = ({ index, children }) => {
  const transition = useMemo(() => ({ duration: 1, delay: 0.2 + index * 0.14, ease: EASE }), [index]);
  return (
    <span className="mask-line">
      <motion.span className="block" initial={LINE_INITIAL} animate={LINE_ANIMATE} transition={transition}>
        {children}
      </motion.span>
    </span>
  );
};

const HeroActions = () => (
  <motion.div initial={FADE_UP_INITIAL} animate={FADE_UP_ANIMATE} transition={T_ACTIONS} className="mt-9 flex flex-wrap items-center gap-4">
    <button
      data-testid="hero-cta-button"
      onClick={() => scrollToSection("contact")}
      className="group rounded-full bg-navy px-7 py-3.5 text-sm font-semibold text-white transition-all hover:bg-navy-light hover:shadow-xl hover:shadow-gold/25"
    >
      Demander un devis gratuit
      <span className="ml-2 inline-block transition-transform group-hover:translate-x-1">→</span>
    </button>
    <a
      data-testid="hero-phone-link"
      href="tel:+33784678802"
      className="flex items-center gap-2 rounded-full border border-navy/15 px-6 py-3.5 text-sm font-semibold text-navy transition-colors hover:border-gold hover:text-gold-dark"
    >
      <Phone size={16} strokeWidth={2.2} /> 07 84 67 88 02
    </a>
  </motion.div>
);

const HeroImage = ({ y }) => (
  <div className="relative lg:col-span-5">
    <motion.div initial={IMG_INITIAL} animate={FADE_UP_ANIMATE} transition={T_IMG}>
      <motion.div style={{ y }} className="relative">
        <div className="overflow-hidden rounded-[2rem] shadow-2xl shadow-navy/30 ring-1 ring-navy/10">
          <img src={HERO_IMG} alt="Professionnelle souriante dans un bureau moderne" className="aspect-[4/5] w-full object-cover object-top" />
          <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-navy/50 via-transparent to-transparent" />
        </div>
      </motion.div>
    </motion.div>
  </div>
);

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: SCROLL_OFFSET });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 80]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -50]);

  return (
    <section id="hero" ref={ref} className="relative overflow-hidden pt-28 pb-16 sm:pt-32 lg:pt-40 lg:pb-24">
      <div className="hero-glow pointer-events-none absolute -top-32 right-[-10%] h-[560px] w-[560px]" />
      <div className="mx-auto grid max-w-7xl items-center gap-12 px-4 sm:px-6 lg:grid-cols-12 lg:gap-8 lg:px-8">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <div className="animate-in" style={{ animationDelay: "0.1s" }}>
            <Eyebrow>Création de sites internet</Eyebrow>
          </div>
          <h1 className="mt-6 font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl lg:text-6xl">
            <HeroLine index={0}>Votre activité mérite</HeroLine>
            <HeroLine index={1}>
              un site à la <span className="text-gold">hauteur</span>
            </HeroLine>
            <HeroLine index={2}>de votre métier.</HeroLine>
          </h1>
          <motion.p initial={FADE_UP_INITIAL} animate={FADE_UP_ANIMATE} transition={T_PARAGRAPH} className="mt-7 max-w-xl text-base leading-relaxed text-slate-600 sm:text-lg">
            Du commerce de quartier au cabinet d'avocat, nous créons des sites internet élégants, rapides et efficaces pour les
            professionnels. Prix transparents, accompagnement humain, zéro jargon.
          </motion.p>
          <HeroActions />
          <motion.p initial={FADE_INITIAL} animate={FADE_ANIMATE} transition={T_NOTE} className="mt-8 font-mono text-xs uppercase tracking-[0.18em] text-slate-500">
            Sites dès 450 € <span className="mx-2 text-gold">•</span> Devis gratuit <span className="mx-2 text-gold">•</span> Sans jargon
          </motion.p>
        </motion.div>
        <HeroImage y={imgY} />
      </div>
    </section>
  );
};
